<template>
  <div class="loading-block" v-if="!this.chartDone"></div>
  <div class="row">
    <div class="col-6 col-xl-4 my-2" v-if="getAllColDescriptionsFormatted.length > 0">
      <div class="card shadow-sm">
        <div class="card-header d-flex align-items-center">
          <p class="text-center h5">{{ $t('selectionData') }}</p>
        </div>
        <div class="card-body">
          <div class="row">
            <template
              v-for="descriptions in getAllColDescriptionsFormatted"
              v-bind:key="descriptions.id"
            >
              <div
                :class="{
                  'col-6': getAllColDescriptionsFormatted.length > 1,
                  col: getAllColDescriptionsFormatted.length === 1,
                }"
              >
                <template v-for="description in descriptions" v-bind:key="description.id">
                  <div class="form-check" v-tooltip :title="getTooltip(description)">
                    <input
                      :disabled="!description.is_suitable_for_analyse"
                      class="form-check-input"
                      type="checkbox"
                      :value="description.col_component_data"
                      :id="description.col_component_data"
                      v-model="checkedDescriptions"
                    />
                    <label class="form-check-label" :for="description.col_component_data">
                      {{ description.col_description }}
                    </label>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="col-6 col-xl-4 my-2" v-if="checkedDescriptions.length != 0">
      <div class="card shadow-sm">
        <div class="card-header text-center">
          <p class="h5">{{ $t('selectionIntervall') }}</p>
        </div>
        <div class="card-body">
          <!-- Desktop: radio buttons -->
          <div class="mt-2 text-center d-none d-md-block">
            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                @click="switchIntervall"
                type="radio"
                name="flexRadioDefault"
                id="selectionLive"
                v-model="selectionIntervall"
                v-bind:value="'live'"
                checked
                :disabled="checkedDescriptions.length === 0"
              />
              <label class="form-check-label" for="selectionLive">
                {{ $t('live_data') }}
              </label>
            </div>
            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                @click="switchIntervall"
                type="radio"
                name="flexRadioDefault"
                id="selectionDay"
                v-model="selectionIntervall"
                v-bind:value="'day'"
                :disabled="checkedDescriptions.length === 0"
              />
              <label class="form-check-label" for="selectionDay">
                {{ $t('day') }}
              </label>
            </div>
            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                @click="switchIntervall"
                type="radio"
                name="flexRadioDefault"
                id="selectionMonth"
                v-model="selectionIntervall"
                v-bind:value="'month'"
                :disabled="checkedDescriptions.length === 0"
              />
              <label class="form-check-label" for="selectionMonth">
                {{ $t('month') }}
              </label>
            </div>
            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                @click="switchIntervall"
                type="radio"
                name="flexRadioDefault"
                id="selectionYear"
                v-model="selectionIntervall"
                v-bind:value="'year'"
                :disabled="checkedDescriptions.length === 0"
              />
              <label class="form-check-label" for="selectionYear">
                {{ $t('year') }}
              </label>
            </div>
          </div>
          <!-- Mobile: chip buttons -->
          <div class="mt-2 d-flex d-md-none flex-wrap gap-2 justify-content-center">
            <button
              v-for="opt in [
                { value: 'live', label: 'Live' },
                { value: 'day', label: 'Tag' },
                { value: 'month', label: 'Monat' },
                { value: 'year', label: 'Jahr' },
              ]"
              :key="opt.value"
              class="btn btn-sm"
              :class="selectionIntervall === opt.value ? 'btn-primary' : 'btn-outline-secondary'"
              :disabled="checkedDescriptions.length === 0"
              @click="
                selectionIntervall = opt.value;
                switchIntervall();
              "
            >
              {{ opt.label }}
            </button>
          </div>
          <hr />
          <div class="mt-2 text-center">
            <Datepicker
              v-model="date"
              range
              closeOnScroll
              autoApply
              :format="getDatepickerFormat"
              locale="de"
              cancelText="abbrechen"
              selectText="auswählen"
              :enableTimePicker="false"
              v-if="selectionIntervall === 'day'"
            />
            <Datepicker
              v-model="date"
              monthPicker
              range
              closeOnScroll
              autoApply
              :format="getDatepickerFormat"
              locale="de"
              cancelText="abbrechen"
              selectText="auswählen"
              :enableTimePicker="false"
              v-if="selectionIntervall === 'month'"
            />
            <Datepicker
              v-model="date"
              yearPicker
              closeOnScroll
              autoApply
              :format="getDatepickerFormat"
              locale="de"
              cancelText="abbrechen"
              selectText="auswählen"
              :enableTimePicker="false"
              v-if="selectionIntervall === 'year'"
            />
          </div>
          <div class="text-end mt-3">
            <button
              class="btn btn-success mb-0 ms-auto"
              @click="generateChart"
              :disabled="isButtonConfirmDisabled"
            >
              {{ $t('confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <ChartWithExport
      v-bind:chartId="chartDataId"
      v-bind:chartData="chartData"
      v-bind:chartOptions="chartOptions"
      v-on:generateExcel="generateExcel"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ChartWithExport from '@/components/ChartWithExport.vue';
const fileDownload = require('js-file-download');

import { createToast } from '@/assets/js/myToast.js';
export default {
  name: 'AnalyseForPlant',
  components: {
    ChartWithExport,
  },
  props: {
    plant: {
      type: Object,
      required: true,
    },
    component: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      componentIdOld: 0,
      date: null,
      chartDone: true,
      selectionIntervall: 'live',
      checkedDescriptions: [],
      chartDataId: 'test',
      chartDataset: [],
      chartLabelArray: [],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          notifyAfterRender: {
            doAfterRender: () => {
              this.chartDone = true;
            },
          },
        },
      },
    };
  },
  mounted() {
    this.dummyFillCheckedDescription();
  },
  updated() {
    console.log('entry updated');
    if (this.componentIdOld === 0) {
      this.componentIdOld = this.component.id;
    }
    if (this.componentIdOld != this.component.id) {
      this.reset();
      this.componentIdOld = this.component.id;
    }
    console.log('exit updated');
  },
  methods: {
    getTooltip(description) {
      if (!description.is_suitable_for_analyse) {
        if (description.col_data_delimiter) {
          return (
            'Kombinierte Felder können nicht ausgewertet werden! (' + description.col_unit + ')'
          );
        }
      }
      return '';
    },
    reset() {
      console.log('entry reset');
      this.checkedDescriptions = [];
      ((this.selectionIntervall = 'live'), (this.date = null));
      this.chartDataset = [];
      this.chartLabelArray = [];
      this.dummyFillCheckedDescription();
      console.log('exit reset');
    },
    dummyFillCheckedDescription() {
      console.log('entry dummyFillCheckedDescription');

      let descriptions = this.component.component_data_description.filter(
        (description) => description.col_description === 'aktuelle Leistung'
      );
      console.log('descriptions: ');
      console.log(descriptions);
      descriptions.forEach((description) => {
        if (description.col_component_data !== null) {
          this.checkedDescriptions.push(description.col_component_data);
        }
      });

      if (this.checkedDescriptions.length === 0) {
        console.log('keine aktuelle Leistung wir waehlen den ersten aus');
        let descriptions = this.component.component_data_description.filter(
          (description) => description.is_suitable_for_analyse
        );
        if (descriptions.length > 0) {
          this.checkedDescriptions.push(descriptions[0].col_component_data);
        }
      }

      if (this.checkedDescriptions.length > 0) {
        console.log('wir haben daten vorgefiltert!');
        this.generateChart();
      }
      console.log('exit dummyFillCheckedDescription');
    },
    switchIntervall() {
      console.log('entry switchIntervall');
      this.date = null;
      console.log('exit switchIntervall');
    },
    generateChart() {
      console.log('entry generateChart');
      console.log(this.date);
      this.chartDone = false;
      console.log(this.chartDone);
      let startIntervall = {};
      let endIntervall = {};

      if (this.selectionIntervall === 'day') {
        console.log('day');
        startIntervall['day'] = this.date[0].getDate();
        startIntervall['month'] = this.date[0].getMonth();
        startIntervall['year'] = this.date[0].getFullYear();
        endIntervall['day'] = this.date[1].getDate();
        endIntervall['month'] = this.date[1].getMonth();
        endIntervall['year'] = this.date[1].getFullYear();
      }
      if (this.selectionIntervall === 'month') {
        console.log('month');
        startIntervall['day'] = '1';
        startIntervall['month'] = this.date[0]['month'];
        startIntervall['year'] = this.date[0]['year'];
        if (this.date[1] != null) {
          var lastDayOfMonth = new Date(this.date[1]['year'], this.date[1]['month'] + 1, 0);
          endIntervall['day'] = lastDayOfMonth.getDate();
          endIntervall['month'] = this.date[1]['month'];
          endIntervall['year'] = this.date[1]['year'];
        } else {
          lastDayOfMonth = new Date(this.date[0]['year'], this.date[0]['month'] + 1, 0);
          endIntervall['day'] = lastDayOfMonth.getDate();
          endIntervall['month'] = this.date[0]['month'];
          endIntervall['year'] = this.date[0]['year'];
        }
      }
      if (this.selectionIntervall === 'year') {
        console.log('year');
        startIntervall['day'] = '1';
        startIntervall['month'] = '0';
        startIntervall['year'] = this.date;
        endIntervall['day'] = '31';
        endIntervall['month'] = '11';
        endIntervall['year'] = this.date;
      }
      if (this.selectionIntervall === 'live') {
        console.log('year');
        startIntervall['day'] = '1';
        startIntervall['month'] = '0';
        startIntervall['year'] = '1970';
        endIntervall['day'] = '31';
        endIntervall['month'] = '11';
        endIntervall['year'] = '1970';
      }

      const formData = {
        selectionData: this.checkedDescriptions,
        startIntervall: startIntervall,
        endIntervall: endIntervall,
        type: this.selectionIntervall,
      };
      axios
        .post(`/api/v1/components/${this.component.id}/get_data_for_component/`, formData)
        .then((response) => {
          console.log('fertig');
          this.chartDone = false;
          console.log(response.data);
          this.visualiseData(response.data);
        })
        .catch((error) => {
          this.chartDone = true;
          if (error.response) {
            if ('detail' in error.response.data) {
              createToast({ text: this.$t(error.response.data['detail']), type: 'error' });
            }
            console.error(JSON.stringify(error.response.data));
          } else if (error.message) {
            console.error(JSON.stringify(error.message));
          } else {
            console.error(JSON.stringify(error));
          }
        });

      console.log('exit generateChart');
    },
    generateExcel() {
      console.log('entry generateExcel');
      console.log(this.date);

      let startIntervall = {};
      let endIntervall = {};

      if (this.selectionIntervall === 'day') {
        console.log('day');
        startIntervall['day'] = this.date[0].getDate();
        startIntervall['month'] = this.date[0].getMonth();
        startIntervall['year'] = this.date[0].getFullYear();
        endIntervall['day'] = this.date[1].getDate();
        endIntervall['month'] = this.date[1].getMonth();
        endIntervall['year'] = this.date[1].getFullYear();
      }
      if (this.selectionIntervall === 'month') {
        console.log('month');
        startIntervall['day'] = '1';
        startIntervall['month'] = this.date[0]['month'];
        startIntervall['year'] = this.date[0]['year'];
        if (this.date[1] != null) {
          endIntervall['day'] = '31';
          endIntervall['month'] = this.date[1]['month'];
          endIntervall['year'] = this.date[1]['year'];
        } else {
          endIntervall['day'] = '31';
          endIntervall['month'] = this.date[0]['month'];
          endIntervall['year'] = this.date[0]['year'];
        }
      }
      if (this.selectionIntervall === 'year') {
        console.log('year');
        startIntervall['day'] = '1';
        startIntervall['month'] = '0';
        startIntervall['year'] = this.date;
        endIntervall['day'] = '31';
        endIntervall['month'] = '11';
        endIntervall['year'] = this.date;
      }
      if (this.selectionIntervall === 'live') {
        console.log('year');
        startIntervall['day'] = '1';
        startIntervall['month'] = '0';
        startIntervall['year'] = '1970';
        endIntervall['day'] = '31';
        endIntervall['month'] = '11';
        endIntervall['year'] = '1970';
      }

      const formData = {
        selectionData: this.checkedDescriptions,
        startIntervall: startIntervall,
        endIntervall: endIntervall,
        type: this.selectionIntervall,
      };
      axios
        .post(`/api/v1/components/${this.component.id}/get_data_for_component_csv/`, formData)
        .then((response) => {
          console.log('fertig');
          console.log(response.data);
          fileDownload(
            response.data,
            this.plant.name + '_' + this.component.name + `_auswertung.csv`
          );
        })
        .catch((error) => {
          if (error.response) {
            if ('detail' in error.response.data) {
              createToast({ text: this.$t(error.response.data['detail']), type: 'error' });
            }
            console.error(JSON.stringify(error.response.data));
          } else if (error.message) {
            console.error(JSON.stringify(error.message));
          } else {
            console.error(JSON.stringify(error));
          }
        });

      console.log('exit generateExcel');
    },
    visualiseData(componentData) {
      console.log('entry visualiseData');
      this.chartLabelArray = [];
      this.chartDataset = [];
      console.log(this.component);
      this.chartDataId = this.component.name;
      componentData.chart_time_array.forEach((item) => {
        let date = new Date(item);
        if (this.selectionIntervall === 'live') {
          this.chartLabelArray.push(
            (date.getHours() < 10 ? '0' : '') +
              date.getHours() +
              ':' +
              (date.getMinutes() < 10 ? '0' : '') +
              date.getMinutes()
          );
        } else {
          this.chartLabelArray.push(
            date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear()
          );
        }
      });
      console.log('chartLabelArray: ' + this.chartLabelArray);
      this.chartDataset = componentData.datasets;
      console.log('exit visualiseData');
    },
  },
  computed: {
    isButtonConfirmDisabled() {
      if (this.selectionIntervall === 'live') {
        return false;
      }

      if (this.date === null) {
        return true;
      }
      return false;
    },
    getDatepickerFormat() {
      console.log('getDatepickerFormat entry');
      console.log('date:');
      console.log(this.date);
      if (this.selectionIntervall === 'day') {
        console.log('day');
        return 'dd/MM/yyyy';
      }
      if (this.selectionIntervall === 'month') {
        console.log('month');
        return 'MM/yyyy';
      }
      if (this.selectionIntervall === 'year') {
        console.log('year');
        return 'yyyy';
      }
      return '';
    },
    getAllColDescriptionsFormatted() {
      console.log('entry getAllColDescriptionsFormatted');
      console.log('activeComponent:');
      console.log(this.component);
      if (this.component === '') {
        return [];
      }
      let array_length = this.component.component_data_description.length;
      let formatted_array = [];

      if (array_length < 2) {
        formatted_array[0] = this.component.component_data_description;
      } else {
        let descriptions = this.component.component_data_description.slice(
          0,
          Math.round(array_length / 2)
        );
        let descriptions2 = this.component.component_data_description.slice(
          Math.round(array_length / 2),
          array_length
        );
        formatted_array[0] = descriptions;
        formatted_array[1] = descriptions2;
        console.log('descriptions');
        console.log(descriptions);
        console.log('descriptions2');
        console.log(descriptions2);
      }
      console.log('formatted_array');
      console.log(formatted_array);
      console.log('exit getAllColDescriptionsFormatted');
      return formatted_array;
    },
    chartData() {
      if (this.chartLabelArray === []) {
        return {};
      }

      console.log('this.chartLabelArray');
      console.log(this.chartLabelArray);
      console.log('this.chartDataset');
      console.log(this.chartDataset);
      return {
        labels: this.chartLabelArray,

        datasets: this.chartDataset,
      };
    },
  },
};
</script>
